import React, { useState, useEffect } from "react";
import { ZCheckboxGroup } from "@atoms";
import { CheckboxValueType } from "antd/es/checkbox/Group";
import {
	MainCheckListStageProps,
	HighlightedCheckListStageProps,
	ExploreCheckListStageProps,
	CheckBoxLabelProps,
	ModuleClassMap,
	CheckListBarOptions
} from "./interface";
import { Space } from "antd";
import ZIcon from "@atoms/ZIcon";
import {
	capitalizeAfterUnderscore,
	capitalizeFirstLetter
} from "@/Utils";
import { EColors } from "@atoms/ZColors/interface";
import { GuideTooltip } from "@organisms/ZOnboarding/ZGuideUI";

// Return the label for step checkbox
const addLabelForCheckbox = ({
	isLabelRequired,
	moduleName,
	isToolTipRequired = false,
	isTooltipVisible = false,
	handleTooltipClose
}: CheckBoxLabelProps) => {
	const iconClassName = ModuleClassMap[moduleName];

	return isToolTipRequired ? (
		<GuideTooltip
			description={`Click Here To Explore ${capitalizeFirstLetter(
				moduleName
			)}`}
			placement="top"
			visible={isTooltipVisible}
			closeOnClickOut={false}
			hasArrow={true}
			buttonText="Ok"
			width={240}
			handleClose={handleTooltipClose}
			closeOnButtonClick={true}
		>
			<Space className="h-16">
				<ZIcon
					name={iconClassName}
					size="md"
					colorVariant={2}
					fill={false}
					color={EColors.GRAY}
				/>
				{isLabelRequired && `Explore ${moduleName?.toLowerCase()}`}
			</Space>
		</GuideTooltip>
	) : (
		<Space>
			<ZIcon
				name={iconClassName}
				size="md"
				colorVariant={2}
				fill={false}
				color={EColors.GRAY}
			/>
			{isLabelRequired && `Explore ${moduleName?.toLowerCase()}`}
		</Space>
	);
};

/**
 * It will return main stage of the checklist guide
 */
export const MainCheckListStage: React.FC<MainCheckListStageProps> = ({
	handleMainCheckListGuideChange,
	moduleOrder,
	checkListBarData
}) => {
	const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);

	const handleTooltipClose = () => {
		setIsTooltipVisible(false);
	};

	useEffect(() => {
		const delay = window.setTimeout(() => {
			setIsTooltipVisible(true);
		}, 10000);
		return () => {
			window.clearTimeout(delay);
		};
	}, []);

	const guideStage = checkListBarData?.modules ?? [];

	const completedGuide: CheckboxValueType[] = guideStage
		.map((item, index) => {
			return item.is_complete && `main_${index}`;
		})
		.filter(Boolean);
	const nextModule: number = guideStage.findIndex(stage => {
		return !stage.is_complete;
	});

	const areAllModuleCompleted = guideStage.every(stage => stage.is_complete);

	const guideOptions: CheckListBarOptions[] = guideStage.map(
		(_item, index) => {
			return {
				label:
					index === nextModule &&
					index === 1 &&
					!checkListBarData?.is_checklist_complete
						? addLabelForCheckbox({
								moduleName: moduleOrder[index],
								isLabelRequired: true,
								isToolTipRequired: true,
								isTooltipVisible,
								handleTooltipClose
						  })
						: addLabelForCheckbox({
								moduleName: moduleOrder[index],
								isLabelRequired: true
						  }),
				value: `main_${index}`,
				labelClass: index === nextModule ? "next-upcoming-module" : "",
				disabled: !(index === nextModule || areAllModuleCompleted)
			};
		}
	);

	return (
		<ZCheckboxGroup
			onChange={e =>
				handleMainCheckListGuideChange(e, areAllModuleCompleted)
			}
			name="main-checklist-bar"
			vertical={false}
			options={guideOptions ?? []}
			value={completedGuide ?? []}
			className="main-checklist-bar"
			space={16}
		/>
	);
};

/**
 * It will return the stage when user just enter to any module
 */
export const HighlightedCheckListStage: React.FC<
	HighlightedCheckListStageProps
> = ({ checkListBarData, moduleOrder, moduleStageMap }) => {
	const guideStage = checkListBarData?.modules ?? [];
	const activeModule = checkListBarData?.active_module;

	const getHighlightedStageLabel = (activeModule: number) => {
		return (
			<ExploreCheckListStage
				checkListBarData={checkListBarData}
				moduleOrder={moduleOrder}
				activeGuide={activeModule}
				moduleStageMap={moduleStageMap}
			/>
		);
	};

	const guideOptions: CheckListBarOptions[] = guideStage.map(
		(_item, index) => {
			return {
				label:
					activeModule !== index
						? addLabelForCheckbox({
								isLabelRequired: false,
								moduleName: moduleOrder[index]
						  })
						: getHighlightedStageLabel(index),
				value: activeModule !== index ? `highlighted_${index}` : "",
				labelClass:
					activeModule !== index ? "" : "highlighted-module-label"
			};
		}
	);

	const completedGuide: CheckboxValueType[] = guideStage
		.map((item, index) => {
			return item.is_complete && `highlighted_${index}`;
		})
		.filter(Boolean);

	return (
		<ZCheckboxGroup
			name="highlighted-bar"
			vertical={false}
			options={guideOptions}
			value={completedGuide}
			disabled={true}
			space={16}
		/>
	);
};

/**
 * It will return the stage when user started to explore the module
 */
export const ExploreCheckListStage: React.FC<ExploreCheckListStageProps> = ({
	activeGuide,
	moduleOrder,
	checkListBarData,
	moduleStageMap
}) => {
	const activeModule = activeGuide ?? checkListBarData?.active_module ?? 0;
	const guideStage = checkListBarData?.modules?.[activeModule].stages ?? [];
	const moduleStage = moduleStageMap?.[moduleOrder[activeModule]] ?? [];

	const completedGuide: CheckboxValueType[] = moduleStage.map((_, index) => {
		return (
			guideStage[index] &&
			guideStage[index].is_complete &&
			`explore_${index}`
		);
	});

	const currentActiveStep: number = moduleStage.findIndex((_stage, index) => {
		return guideStage[index] && !guideStage[index].is_complete;
	});

	const guideOptions: CheckListBarOptions[] = moduleStage.map(
		(_stage, index) => {
			const moduleStageLabel = moduleStage?.[index]?.includes(
				"introduction"
			)
				? moduleStage?.[index].split("_").pop()
				: moduleStage?.[index];

			return {
				label: capitalizeAfterUnderscore(moduleStageLabel),
				value: `explore_${index}`,
				labelClass:
					currentActiveStep === index ||
					guideStage[index]?.is_complete
						? "opcacity-100"
						: "opacity-25"
			};
		}
	);

	return (
		<>
			{activeModule !== null && !!moduleOrder.length && (
				<div
					className={`inline font-medium leading-none capitalize ${
						activeGuide !== undefined ? "mr-3" : "mr-2"
					}`}
				>
					{addLabelForCheckbox({
						isLabelRequired: true,
						moduleName: moduleOrder[activeModule]
					})}
				</div>
			)}
			<ZCheckboxGroup
				name="explore-bar"
				vertical={false}
				options={guideOptions}
				value={completedGuide}
				disabled={true}
				space={8}
				className={`${
					activeGuide !== undefined
						? "highlighted-group"
						: "inner-stage-group"
				}`}
			/>
		</>
	);
};
